import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, fromEvent } from 'rxjs';

import { environment } from '../../../environments/environment';

const LOG_PREFIX = '[SwUpdate]';
const MOBILE_VIEWPORT_MAX_PX = 768;

@Injectable({ providedIn: 'root' })
export class SwUpdateService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swUpdate = inject(SwUpdate);

  private updateCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private waitingVersion = false;

  readonly autoUpdate = environment.autoUpdate;
  readonly updateAvailable = signal(false);
  readonly updating = signal(false);

  constructor() {
    if (!isPlatformBrowser(this.platformId) || !this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.waitingVersion = true;
        console.info(LOG_PREFIX, 'update available', {
          autoUpdate: this.autoUpdate,
          prompt: this.shouldPromptForUpdate(),
          current: event.currentVersion.hash,
          latest: event.latestVersion.hash,
        });

        this.promptOrApplyWaitingVersion();
      });

    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      console.error(LOG_PREFIX, 'unrecoverable state; reloading', event);
      this.reload();
    });

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          void this.checkForUpdate();
        }
      });

    fromEvent(window, 'focus')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.checkForUpdate();
      });

    void this.checkForUpdate();

    this.updateCheckIntervalId = setInterval(() => {
      void this.checkForUpdate();
    }, environment.updateCheckIntervalMs);

    this.destroyRef.onDestroy(() => {
      if (this.updateCheckIntervalId !== null) {
        clearInterval(this.updateCheckIntervalId);
        this.updateCheckIntervalId = null;
      }
    });
  }

  async checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled || this.updating()) {
      return false;
    }

    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      if (updateFound) {
        console.info(LOG_PREFIX, 'checkForUpdate: update found');
      }
      if (this.waitingVersion && this.shouldPromptForUpdate()) {
        this.updateAvailable.set(true);
      }
      return updateFound;
    } catch (error) {
      console.error(LOG_PREFIX, 'checkForUpdate failed', error);
      return false;
    }
  }

  async applyUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled || this.updating()) {
      return;
    }

    console.info(LOG_PREFIX, 'applyUpdate: activating');
    this.updating.set(true);
    this.updateAvailable.set(false);

    try {
      await this.swUpdate.activateUpdate();
      console.info(LOG_PREFIX, 'applyUpdate: activated; reloading');
      this.reload();
    } catch (error) {
      console.error(LOG_PREFIX, 'applyUpdate failed', error);
      this.updating.set(false);

      if (this.shouldPromptForUpdate()) {
        this.updateAvailable.set(true);
      }
    }
  }

  dismiss(): void {
    this.updateAvailable.set(false);
  }

  private promptOrApplyWaitingVersion(): void {
    if (this.shouldPromptForUpdate()) {
      this.updateAvailable.set(true);
      return;
    }

    console.info(LOG_PREFIX, 'auto-update: applying waiting version');
    void this.applyUpdate();
  }

  /** Desktop auto-updates; mobile always prompts so the new-deploy popup can show. */
  private shouldPromptForUpdate(): boolean {
    return !this.autoUpdate || this.isMobileViewport();
  }

  private isMobileViewport(): boolean {
    return isPlatformBrowser(this.platformId) && window.innerWidth <= MOBILE_VIEWPORT_MAX_PX;
  }

  private reload(): void {
    document.location.reload();
  }
}
